import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';

// Liste initiale des domaines d'activité (peut être chargée depuis une API)
const INITIAL_ACTIVITY_DOMAINS = [
  'Transport et logistique',
  'Commerce général',
  'Import/Export',
  'Agriculture',
  'Construction',
  'Industrie manufacturière',
  'Services financiers',
  'Technologie et IT',
  'Santé et médical',
  'Éducation et formation',
  'Hôtellerie et restauration',
  'Immobilier',
  'Énergie',
  'Télécommunications',
  'Commerce de détail',
];

// Liste des domaines de transport pour les transporteurs
const TRANSPORT_DOMAINS = [
  'Transport de marchandises lourdes',
  'Transport de colis légers',
  'Transport de personnes',
  'Service de messagerie/express',
  'Transport frigorifique',
  'Transport de matières dangereuses',
  'Transport de matériaux de construction',
  'Transport agricole',
  'Transport minier',
  'Transport de carburant',
  'Transport de déchets',
  'Déménagement/Transport de meubles',
];

const UserFormDialog = ({ open, onClose, onSubmit, user, isLoading }) => {
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    role: 'CLIENT',
    status: 'ACTIVE',
    is_active: true,
    profile: {},
  });

  const [errors, setErrors] = React.useState({});
  const [activityDomains, setActivityDomains] = React.useState(INITIAL_ACTIVITY_DOMAINS);
  const [transportDomains, setTransportDomains] = React.useState(TRANSPORT_DOMAINS);

  React.useEffect(() => {
    if (user) {
      // Déterminer le profil selon le rôle
      let profileData = {};
      if (user.role === 'CLIENT' && user.client_profile) {
        profileData = user.client_profile;
      } else if (user.role === 'TRANSPORTEUR' && user.transporter_profile) {
        profileData = user.transporter_profile;
      } else if (user.role === 'AGENT' && user.agent_profile) {
        profileData = user.agent_profile;
      }

      setFormData({
        email: user.email || '',
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number || '',
        role: user.role || 'CLIENT',
        status: user.status || 'PENDING',
        is_active: user.is_active ?? true,
        profile: profileData,
      });
    } else {
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        role: 'CLIENT',
        status: 'ACTIVE',
        is_active: true,
        profile: {},
      });
    }
    setErrors({});
  }, [user, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Si on change le rôle, réinitialiser le profil
      if (field === 'role') {
        newData.profile = {};
      }

      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleProfileChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        [field]: value,
      },
    }));
    if (errors[`profile.${field}`]) {
      setErrors((prev) => ({ ...prev, [`profile.${field}`]: null }));
    }
  };

  const handleAddCustomDomain = (newDomain) => {
    if (!activityDomains.includes(newDomain)) {
      setActivityDomains(prev => [...prev, newDomain]);
    }
  };

  const handleAddCustomTransportDomain = (newDomain) => {
    if (!transportDomains.includes(newDomain)) {
      setTransportDomains(prev => [...prev, newDomain]);
    }
  };

  const validate = () => {
    const newErrors = {};

    const emailTrim = (formData.email || '').trim();
    if (emailTrim && !/\S+@\S+\.\S+/.test(emailTrim)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!user && !formData.password) {
      newErrors.password = 'Le mot de passe est requis pour un nouvel utilisateur';
    } else if (!user && formData.password && formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.first_name) {
      newErrors.first_name = 'Le prénom est requis';
    }

    const phoneTrim = (formData.phone_number || '').trim();
    if (!phoneTrim) {
      newErrors.phone_number = 'Le numéro de téléphone est requis';
    } else {
      const digits = phoneTrim.replace(/\D/g, '');
      if (digits.length < 8) {
        newErrors.phone_number = 'Le numéro doit contenir au moins 8 chiffres';
      }
    }

    // Validations conditionnelles selon le rôle
    if (formData.role === 'CLIENT') {
      if (!formData.profile.type_client) {
        newErrors['profile.type_client'] = 'Le type de client est requis';
      }

      // Si c'est une entreprise, le nom est requis
      if (formData.profile.type_client === 'entreprise' && !formData.profile.company_name) {
        newErrors['profile.company_name'] = 'Le nom de l\'entreprise est requis';
      }

      // Les domaines d'activité sont requis pour les entreprises
      if (formData.profile.type_client === 'entreprise' && (!formData.profile.domaine_activites || formData.profile.domaine_activites.length === 0)) {
        newErrors['profile.domaine_activites'] = 'Au moins un domaine d\'activité est requis';
      }
    } else if (formData.role === 'TRANSPORTEUR') {
      // Si c'est une entreprise, le nom de l'organisation est requis
      if (formData.profile.is_company && !formData.profile.organisation_name) {
        newErrors['profile.organisation_name'] = 'Le nom de l\'organisation est requis';
      }
      // Les domaines de transport sont requis
      if (!formData.profile.transport_domains || formData.profile.transport_domains.length === 0) {
        newErrors['profile.transport_domains'] = 'Au moins un domaine de transport est requis';
      }
    } else if (formData.role === 'AGENT') {
      if (!formData.profile.role_level) {
        newErrors['profile.role_level'] = 'Le niveau de rôle est requis';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const submitData = { ...formData };
      const trimmedEmail = (submitData.email || '').trim();
      submitData.email = trimmedEmail ? trimmedEmail : null;

      if (submitData.profile == null) {
        delete submitData.profile;
      } else if (submitData.profile && typeof submitData.profile === 'object') {
        const tc = submitData.profile.type_client;
        if (tc === 'particulier') submitData.profile.type_client = 'INDIVIDUAL';
        if (tc === 'entreprise') submitData.profile.type_client = 'BUSINESS';
      }

      // Don't send password if it's empty (edit mode)
      if (user && !submitData.password) {
        delete submitData.password;
      }
      // L’API de mise à jour n’expose pas l’email (identité = téléphone)
      if (user) {
        delete submitData.email;
      }

      // Only send profile data if it's not empty and not for ADMIN
      if (Object.keys(submitData.profile).length === 0 || submitData.role === 'ADMIN') {
        delete submitData.profile;
      }

      onSubmit(submitData);
    }
  };

  const renderProfileFields = () => {
    switch (formData.role) {
      case 'CLIENT':
        return (
          <div className="space-y-4">
            {/* Type de client - OBLIGATOIRE */}
            <div className="space-y-2">
              <Label htmlFor="type_client">
                Type de client <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.profile.type_client || ''}
                onValueChange={(value) => handleProfileChange('type_client', value)}
              >
                <SelectTrigger className={cn(errors['profile.type_client'] && 'border-destructive')}>
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="particulier">Particulier</SelectItem>
                  <SelectItem value="entreprise">Entreprise</SelectItem>
                </SelectContent>
              </Select>
              {errors['profile.type_client'] && (
                <p className="text-sm text-destructive">{errors['profile.type_client']}</p>
              )}
            </div>

            {/* Nom de l'entreprise - Conditionnel (si entreprise) */}
            {formData.profile.type_client === 'entreprise' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company_name">
                    Nom de l'entreprise <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="company_name"
                    value={formData.profile.company_name || ''}
                    onChange={(e) => handleProfileChange('company_name', e.target.value)}
                    placeholder="Ex: LogiTrans SARL"
                    className={cn(errors['profile.company_name'] && 'border-destructive', 'bg-background')}
                  />
                  {errors['profile.company_name'] && (
                    <p className="text-sm text-destructive">{errors['profile.company_name']}</p>
                  )}
                </div>

                {/* Domaines d'activité - Multi-select avec ajout personnalisé */}
                <div className="space-y-2">
                  <Label htmlFor="domaine_activites">
                    Domaines d'activité <span className="text-destructive">*</span>
                  </Label>
                  <MultiSelect
                    options={activityDomains}
                    value={formData.profile.domaine_activites || []}
                    onChange={(value) => handleProfileChange('domaine_activites', value)}
                    placeholder="Sélectionner les domaines..."
                    allowCustom={true}
                    onAddCustom={handleAddCustomDomain}
                    className={cn(errors['profile.domaine_activites'] && 'border-destructive')}
                  />
                  {errors['profile.domaine_activites'] && (
                    <p className="text-sm text-destructive">{errors['profile.domaine_activites']}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Sélectionnez un ou plusieurs domaines. Vous pouvez ajouter un domaine personnalisé si nécessaire.
                  </p>
                </div>
              </>
            )}

            {/* Adresse - Optionnel */}
            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={formData.profile.address || ''}
                onChange={(e) => handleProfileChange('address', e.target.value)}
                placeholder="Adresse complète"
                className="bg-background"
              />
            </div>
          </div>
        );

      case 'TRANSPORTEUR':
        return (
          <div className="space-y-4">
            {/* Est une entreprise */}
            <div className="space-y-2">
              <Label htmlFor="is_company">Type</Label>
              <Select
                value={formData.profile.is_company ? 'true' : 'false'}
                onValueChange={(value) => handleProfileChange('is_company', value === 'true')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Indépendant</SelectItem>
                  <SelectItem value="true">Entreprise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Domaines de transport */}
            <div className="space-y-2">
              <Label htmlFor="transport_domains">
                Domaines de transport <span className="text-destructive">*</span>
              </Label>
              <MultiSelect
                options={transportDomains}
                value={formData.profile.transport_domains || []}
                onChange={(value) => handleProfileChange('transport_domains', value)}
                placeholder="Sélectionner les domaines de transport..."
                allowCustom={true}
                onAddCustom={handleAddCustomTransportDomain}
                className={cn(errors['profile.transport_domains'] && 'border-destructive')}
              />
              {errors['profile.transport_domains'] && (
                <p className="text-sm text-destructive">{errors['profile.transport_domains']}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Sélectionnez tous les types de transport que ce transporteur propose
              </p>
            </div>

            {/* Nom de l'organisation - Conditionnel (si entreprise) */}
            {formData.profile.is_company && (
              <div className="space-y-2">
                <Label htmlFor="organisation_name">
                  Nom de l'organisation <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="organisation_name"
                  value={formData.profile.organisation_name || ''}
                  onChange={(e) => handleProfileChange('organisation_name', e.target.value)}
                  placeholder="Ex: Transports Rapides SARL"
                  className={cn(errors['profile.organisation_name'] && 'border-destructive', 'bg-background')}
                />
                {errors['profile.organisation_name'] && (
                  <p className="text-sm text-destructive">{errors['profile.organisation_name']}</p>
                )}
              </div>
            )}

            {/* Champs supplémentaires pour entreprises */}
            {formData.profile.is_company && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Numéro d'enregistrement</Label>
                  <Input
                    id="registration_number"
                    value={formData.profile.registration_number || ''}
                    onChange={(e) => handleProfileChange('registration_number', e.target.value)}
                    placeholder="RCCM/GN..."
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tax_info">Numéro fiscal (NIF)</Label>
                  <Input
                    id="tax_info"
                    value={formData.profile.tax_info || ''}
                    onChange={(e) => handleProfileChange('tax_info', e.target.value)}
                    placeholder="NIF..."
                    className="bg-background"
                  />
                </div>
              </>
            )}

            {/* Statut de vérification */}
            <div className="space-y-2">
              <Label htmlFor="verification_status">Statut de vérification</Label>
              <Select
                value={formData.profile.verification_status || 'UNVERIFIED'}
                onValueChange={(value) => handleProfileChange('verification_status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UNVERIFIED">Non vérifié</SelectItem>
                  <SelectItem value="PENDING">En attente</SelectItem>
                  <SelectItem value="VERIFIED">Vérifié</SelectItem>
                  <SelectItem value="REJECTED">Rejeté</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'AGENT':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role_level">
                Niveau de rôle <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.profile.role_level || ''}
                onValueChange={(value) => handleProfileChange('role_level', value)}
              >
                <SelectTrigger className={cn(errors['profile.role_level'] && 'border-destructive')}>
                  <SelectValue placeholder="Sélectionner le niveau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="SUPERVISOR">Superviseur</SelectItem>
                </SelectContent>
              </Select>
              {errors['profile.role_level'] && (
                <p className="text-sm text-destructive">{errors['profile.role_level']}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.profile.notes || ''}
                onChange={(e) => handleProfileChange('notes', e.target.value)}
                placeholder="Notes internes..."
                className="bg-background"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto bg-card text-foreground">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {user ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {user
              ? 'Modifiez les informations de l\'utilisateur ci-dessous.'
              : 'Remplissez le formulaire pour créer un nouvel utilisateur. Les champs requis sont marqués d\'un astérisque (*).'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-foreground border-b pb-2">Informations générales</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">
                  Prénom <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="Votre Prenom"
                  className={cn(errors.first_name && 'border-destructive', 'bg-background')}
                />
                {errors.first_name && (
                  <p className="text-sm text-destructive">{errors.first_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Nom</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="Kamano"
                  className="bg-background"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="exemple@gmail.com (optionnel)"
                disabled={!!user}
                className={cn(errors.email && 'border-destructive', 'bg-background')}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              {!user && (
                <p className="text-xs text-muted-foreground">
                  Facultatif. L’identifiant principal est le numéro de téléphone.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                Mot de passe {!user && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={user ? 'Laisser vide pour ne pas changer' : 'Minimum 8 caractères'}
                className={cn(errors.password && 'border-destructive', 'bg-background')}
              />
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              {user && (
                <p className="text-xs text-muted-foreground">
                  Laissez vide si vous ne souhaitez pas changer le mot de passe
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">
                Téléphone <span className="text-destructive">*</span>
              </Label>
              <Input
                id="phone_number"
                value={formData.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                placeholder="+224 627 61 38 35"
                className={cn(errors.phone_number && 'border-destructive', 'bg-background')}
              />
              {errors.phone_number && (
                <p className="text-sm text-destructive">{errors.phone_number}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Obligatoire. Indicatif pays inclus si besoin (ex. +224). Au moins 8 chiffres.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">
                  Rôle <span className="text-destructive">*</span>
                </Label>
                <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLIENT">Client</SelectItem>
                    <SelectItem value="TRANSPORTEUR">Transporteur</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                    <SelectItem value="ADMIN">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">
                  Statut <span className="text-destructive">*</span>
                </Label>
                <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">En attente</SelectItem>
                    <SelectItem value="ACTIVE">Actif</SelectItem>
                    <SelectItem value="SUSPENDED">Suspendu</SelectItem>
                    <SelectItem value="BANNED">Banni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Compte actif
              </Label>
            </div>
          </div>

          {/* Informations de profil spécifiques au rôle */}
          {formData.role !== 'ADMIN' && (
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="font-semibold text-lg text-foreground border-b pb-2">
                Informations de profil ({formData.role})
              </h3>
              {renderProfileFields()}
            </div>
          )}

          <DialogFooter className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Annuler
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Enregistrement...' : user ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserFormDialog;
